export { default as RadioGroup } from "@mui/material/RadioGroup";
import ErrorIcon from "@mui/icons-material/Error";
import Box from "@mui/material/Box";
import MuiRadioGroup, { RadioGroupProps as MuiRadioGroupProps } from "@mui/material/RadioGroup";
import { styled } from "@mui/material/styles";
import { FlexRow, FormControlLabel, Radio, Text } from "designSystem/index";
import { black, grey, red } from "designSystem/styles/color";
import { disabledCursorStyling } from "designSystem/styles/style-functions";
import React, { FunctionComponent, ReactElement, ReactNode } from "react";

const OuterBox = styled(Box)(({ theme }) => ({
	display: "inline-block",
	textAlign: "start",
}));

const InvalidText = styled(Text)(({ theme }) => ({
	color: red[600],
	textAlign: "center",
	paddingLeft: 5,
}));

const TitleBox = styled(Box)(({ theme }) => ({
	display: "block",
	paddingBottom: 9,
	textAlign: "start",
}));

const TitleText = styled(Text)(({ theme }) => ({
	color: black[500],
	fontWeight: 700,
	marginBottom: 8,
	textAlign: "start",
}));

interface RadioProperties {
	/** Whether the node should be disabled */
	disabled?: boolean;

	/** Helper text to be displayed below the node name */
	helperText?: string;

	/** Resource to be used for text */
	helperTextRes?: string;

	/** Params for resource to be used for text */
	helperTextResParams?: Array<number | string>;

	/** 
	 * Content of Option - to allow for any other content to be added under the label
	 * props should already be passed to this node
	 */
	labelContent?: ReactNode;

	/** label icon for the option */
	labelIcon?: ReactNode;
}

type RadioOptionValue = number | string | boolean;

export interface RadioOption<T extends RadioOptionValue> extends RadioProperties {
	/** Name of the node */
	name?: string;

	/** Whether the value is invalid */
	invalid?: boolean;

	/** Value to be used as the node's id */
	value: T;
}

export interface RadioGroupProps<T extends RadioOptionValue> extends Omit<MuiRadioGroupProps, "onChange"> {
	/** If the group is invalid, text to be displayed below it */
	invalidText?: string;

	/** Function to be called on selection change */
	onChange: (event: any, value: T) => void;

	/** List of nodes composing the group */
	options: RadioOption<T>[];
}

interface OptionLabelProps extends RadioProperties {
	/** Name of the node */
	label: string;

	/** Value to be used as the id in the DOM */
	nodeId: string;
}

const OptionLabel: FunctionComponent<OptionLabelProps> = (props) => {
	const {
		disabled,
		helperText,
		helperTextRes,
		helperTextResParams,
		label,
		labelContent,
		labelIcon,
		nodeId,
	} = props;
	const cursorStyling = disabledCursorStyling(disabled);

	return (
		<Box sx={{ display: "inline-block" }}>
			<label htmlFor={nodeId} style={{ display: "none" }}>{label}</label>
			<FlexRow>
				{labelIcon && (<span style={{ paddingRight: "8px" }}>{labelIcon}</span>)}
				<Text
					sx={{
						cursor: cursorStyling,
						display: "block",
						lineHeight: "16px",
						textAlign: "left",
					}}
					text={label}
				/>
			</FlexRow>
			{(helperText || helperTextRes) && (
				<Text
					res={helperTextRes}
					resParams={helperTextResParams}
					sx={{
						color: disabled ? grey[800] : black[500],
						cursor: cursorStyling,
						display: "block",
						lineHeight: "16px",
						textAlign: "left",
					}}
					text={helperText}
				/>
			)}
			{labelContent}
		</Box>
	);
};

function RadioGroup<T extends RadioOptionValue>(props: RadioGroupProps<T>): ReactElement<any, any> | null {
	const {
		className,
		invalidText,
		onChange,
		options,
		...otherProps
	} = props;

	const useOnChange = (event: any, value: string) => {
		if (typeof props.value === "boolean") {
			onChange(event, (value === "true") as any as T);
		} else if (typeof props.value === "number") {
			onChange(event, parseFloat(value) as any as T);
		} else {
			onChange(event, value as any as T);
		}
	};

	return (
		<OuterBox data-csi-automation="RadioGroup">
			{(props.name || invalidText) && (
				<TitleBox>
					{props.name &&
						<TitleText text={props.name} />}
					{props.invalidText &&
						<Text sx={{ color: red[600] }} text="*" />}
				</TitleBox>
			)}
			<MuiRadioGroup
				onChange={useOnChange}
				{...otherProps}
			>
				{options.map((option) => (
					<FormControlLabel
						className="csi-outer-radio-label"
						control={(
							<Radio
								checked={option.value == props.value}
								disabled={option.disabled}
								id={option.value.toString()}
								invalid={option.invalid}
							/>
						)}
						key={option.value.toString()}
						label={(
							<OptionLabel
								disabled={option.disabled}
								helperText={option.helperText}
								helperTextRes={option.helperTextRes}
								label={option.name}
								labelContent={option.labelContent}
								labelIcon={option.labelIcon}
								nodeId={option.value.toString()}
							/>
						)}
						labelPlacement="end"
						sx={{
							alignItems: "start",
							cursor: disabledCursorStyling(option.disabled),
							paddingLeft: 1,
						}}
						value={option.value}
					/>
				))}
			</MuiRadioGroup>
			{props.invalidText && (
				<Box sx={{ marginTop: 1, textAlign: "start" }}>
					<ErrorIcon
						color="error"
						height="10px"
						width="10px"
					/>
					<InvalidText text={props.invalidText} />
				</Box>
			)}
		</OuterBox>
	);
};

export default RadioGroup;